import { plainToInstance } from 'class-transformer';

import { instance } from '@/api/config';
import { IAgreementsPostDto, IAgreementsResponse } from '@/types/agreements';
import { IAnnouncementPostDto, IAnnouncementResponse } from '@/types/announcement';
import { IAppsPostDto } from '@/types/apps';
import {
  IAuthEmailStartResponse,
  IAuthEmailVerifyResponse,
  IAuthSmsStartResponse,
  IAuthSmsVerifyResponse,
  IPostAuthEmailStart,
  IPostAuthEmailVerify,
  IPostAuthForgot,
  IPostAuthForgotEnd,
  IPostAuthSmsStart,
  IPostAuthSmsVerify
} from '@/types/auth';
import { IBulkChangeResponse, IPostBulkChangeBodyDto } from '@/types/bulk-change';
import { IBulkEmailPostDto, IBulkEmailResponse, IBulkEmailSuggestRequestDto, IBulkEmailSuggestResponse } from '@/types/bulk-email';
import { IPostCallForwardingBodyDto } from '@/types/call-forwarding';
import { ChatMessagesListResponse, CreateChatMessageRequest } from '@/types/chat-messages-list';
import {
  ChatBotConversationResponse,
  ChatBotCreateConversationRequest,
  ChatBotMessageRequest,
  ChatBotMessageResponse
} from '@/types/chatbtot';
import { IPostCityBodyDto, IPostSubCityBodyDto } from '@/types/city';
import { ICompanyPostDto, ICompanyResponse } from '@/types/companies';
import { ICompanyContactsPostDto } from '@/types/company-contacts';
import { IContainersPostDto, IContainersResponse } from '@/types/containers';
import { IConvertDeliveredToReturnBodyDto, IConvertDeliveredToReturnResponse } from '@/types/convert-delivered';
import { IDamagePackageResponse, IPostDamagePackageBodyDto } from '@/types/damage-package';
import { CreateDistrictDto, DistrictResponseDto } from '@/types/districts';
import { IDocumentPostDto, IDocumentResponse } from '@/types/document';
import { IDoorPostDto } from '@/types/door';
import { ICreateDriverDto, IDriverResponse } from '@/types/drivers';
import {
  IFordIntegrationFetchListResponse,
  IFordIntegrationFetchQueryDto,
  IFordIntegrationParseListResponse
} from '@/types/ford-integration';
import { IPostInventoryBodyDto } from '@/types/inventory';
import { IInviteResponse, INewUserAcceptPostDto } from '@/types/invite';
import {
  IAcceptLossCaseBodyDto,
  ICancelLossCaseBodyDto,
  ICreateDocumentRequestBodyDto,
  ICreateLossBarcodeBodyDto,
  IFromShipmentBodyDto,
  ILossPublicUploadRequestBodyDto,
  ILossPublicUploadStarted,
  ILostPackageDescriptionPostBodyDto,
  IPostLostPackgeBodyDto,
  IReopenLossCaseBodyDto,
  ITransitionLossCaseBodyDto
} from '@/types/lost-packages';
import { IMissionPostDto } from '@/types/mission';
import { CreateMultiSessionUserDTO, MultiSessionUserResponse } from '@/types/multi-session-users';
import { IPackTypesResponse, IPostPackTypesBodyDto } from '@/types/pack-types';
import { IPackageResponse, IPostPackgeBodyDto } from '@/types/package';
import { PackageFilesPostDto, PackageFilesPostResponseDto } from '@/types/package-files';
import { IPackageRequestResponse, IPostPackageRequestBodyDto } from '@/types/package-request';
import { IPackageTanzimPostDto } from '@/types/packageTanzim';
import { IPersonelPostDto } from '@/types/personel';
import { IPersonelLogEntryPostDto, IPersonelLogExitPostDto, IPersonelLogPostDto } from '@/types/personel-log';
import { IPointMapResponse, IPointResponse, IPostPointBodyDto, IPostPointMapBodyDto } from '@/types/point';
import { ICityResponse, ISubCity } from '@/types/province';
import { IRegionContactDetailPostDto, IRegionContactDetailResponse } from '@/types/region-contact-detail';
import { IRegionDetailPostDto, IRegionDetailResponse } from '@/types/region-detail';
import { RegionTransferTypesCreate } from '@/types/region-transfer-types';
import { IPostReturnPackgeBodyDto, IReturnPackageResponse } from '@/types/return-package';
import { IRouteResponse } from '@/types/route';
import { IRouteCachePostDto, IRouteCacheResponse } from '@/types/route-cache';
import { IPostRouteBodyDto, IPostRouteSessionResponseDto, IPostRouteSessionsBodyDto } from '@/types/route-sessions';
import { IPostSessionPersonaleBodyDto } from '@/types/session-personale';
import { IPostSupplierBodyDto, ISupplierResponse } from '@/types/suppliers';
import { TransferVehicleEntryCreate, TransferVehicleEntryResponse } from '@/types/transfer-vehicle-entry';
import { IPostUserBodyDto, IPostUserInviteBodyDto, IUserResponse } from '@/types/user';
import { IPostUserCompanyPermissionsBodyDto, IUserCompanyPermissionResponse } from '@/types/user-company-permissions';
import { IPostUserInsurancePermissionsBodyDto, IUserInsurancePermissionResponse } from '@/types/user-insurance-permissions';
import { IUserInviteResponse } from '@/types/user-invite';
import { IPostUserOperationPermissionsBodyDto, IUserOperationsPermissionResponse } from '@/types/user-operations-permissions';
import { IPostUserVendorPermissionsBodyDto, IUserVendorPermissionResponse } from '@/types/user-vendor-permissions';
import { IPostUserXDockPermissionsBodyDto, IUserXDockPermissionResponse } from '@/types/user-xdock-permission';
import { IPostVehiclePlanBodyDto, IVehiclePlanResponse, IVehiclePlanVehicleStatusPostBodyDto } from '@/types/vehicle-plan';
import { IPostVehicleRequestBodyDto, IVehicleRequestResponse } from '@/types/vehicle-request';
import { IPostVehicleBodyDto, IPostVehicleCategoryBodyDto, IVehicleCategoryResponse, IVehicleResponse } from '@/types/vehicles';
import { IPostVendorBoyDto, IVendorResponse } from '@/types/vendor';
import { IPostWebhooksBodyDto, IWebhooksResponse } from '@/types/webhooks';

export const postInventory = async (body: IPostInventoryBodyDto) => {
  await instance(`inventory`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postRouteSessions = async (body: IPostRouteSessionsBodyDto) => {
  return plainToInstance(
    IPostRouteSessionResponseDto,
    await instance(`routeSessions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postSessionPersonale = async (body: IPostSessionPersonaleBodyDto) => {
  await instance(`sessionPersonale`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPoint = async (body: IPostPointBodyDto) => {
  return plainToInstance(
    IPointResponse,
    await instance(`point`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postDrivers = async (body: ICreateDriverDto) => {
  return plainToInstance(
    IDriverResponse,
    await instance(`drivers`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postFileUploadRouteSessions = async (id: number | string) => {
  return await instance(`routeSessions/${id}/upload`, {
    method: 'POST'
  });
};

export const postFileUploadLostPackages = async (id: number | string) => {
  return await instance(`lostPackages/${id}/upload`, {
    method: 'POST'
  });
};

export const postVendor = async (body: IPostVendorBoyDto) => {
  return plainToInstance(
    IVendorResponse,
    await instance(`vendor`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postPointMap = async (body: IPostPointMapBodyDto) => {
  return plainToInstance(
    IPointMapResponse,
    await instance(`pointmap`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postPackage = async (body: IPostPackgeBodyDto) => {
  return plainToInstance(
    IPackageResponse,
    await instance(`package`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postCity = async (body: IPostCityBodyDto) => {
  return plainToInstance(
    ICityResponse,
    await instance(`city`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postCompany = async (body: ICompanyPostDto) => {
  return await plainToInstance(
    ICompanyResponse,
    instance(`company`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postLostPackage = async (body: IPostLostPackgeBodyDto) => {
  return await plainToInstance(
    ICompanyResponse,
    instance(`lostPackages`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postSubCity = async (body: IPostSubCityBodyDto) => {
  return plainToInstance(
    ISubCity,
    await instance(`subCity`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postReturnPackage = async (body: IPostReturnPackgeBodyDto) => {
  return plainToInstance(
    IReturnPackageResponse,
    await instance(`returnPackage`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postDamagePackage = async (body: IPostDamagePackageBodyDto) => {
  return plainToInstance(
    IDamagePackageResponse,
    await instance(`damagePackage`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postPackTypes = async (body: IPostPackTypesBodyDto) => {
  return plainToInstance(
    IPackTypesResponse,
    await instance(`packType`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postVehicle = async (body: IPostVehicleBodyDto) => {
  return plainToInstance(
    IVehicleResponse,
    await instance(`vehicles`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postUser = async (body: IPostUserBodyDto) => {
  return plainToInstance(
    IUserResponse,
    await instance(`user`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

/** SUPERADMIN bypass: kullanıcının telefonunu doğrulanmış işaretler (SMS adımı atlanır) */
export const postUserVerifyPhone = async (id: number) => {
  return plainToInstance(IUserResponse, await instance(`user/${id}/verify-phone`, { method: 'POST' }));
};

/** SUPERADMIN bypass: kullanıcının e-postasını doğrulanmış işaretler (e-posta adımı atlanır) */
export const postUserVerifyEmail = async (id: number) => {
  return plainToInstance(IUserResponse, await instance(`user/${id}/verify-email`, { method: 'POST' }));
};

export const postUserInvite = async (body: IPostUserInviteBodyDto) => {
  return plainToInstance(
    IUserInviteResponse,
    await instance(`userInvite`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postUserInviteResendById = async (id: string) => {
  return plainToInstance(
    IUserInviteResponse,
    await instance(`userInvite/${id}/resend`, {
      method: 'POST'
    })
  );
};

export const postUserCompanyPermissions = async (body: IPostUserCompanyPermissionsBodyDto) => {
  return plainToInstance(
    IUserCompanyPermissionResponse,
    await instance(`userCompanyPermissions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postUserXDockPermissions = async (body: IPostUserXDockPermissionsBodyDto) => {
  return plainToInstance(
    IUserXDockPermissionResponse,
    await instance(`userXdockPermissions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postRouteCache = async (body: IRouteCachePostDto) => {
  return plainToInstance(
    IRouteCacheResponse,
    await instance(`route-cache`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

/** Rota Düzenle: kayıttan çıkarılan bacağın sonucu (cityapi RouteCacheRetireService) */
export interface IRetireLegResult {
  from: number;
  to: number;
  edgeId?: number;
  routeId?: number;
  routeName?: string | null;
  retired: boolean;
  reasons: string[];
}

/** Çıkarılan bacaklar: rota hiçbir yerde kullanılmıyorsa rota hidden=1 + kenar pasif (BE kontrol eder) */
export const postRouteCacheRetireLegs = async (legs: { from: number; to: number }[]) =>
  (await instance(`route-cache/retire-legs`, {
    method: 'POST',
    body: JSON.stringify({ legs })
  })) as { results: IRetireLegResult[] };

export const postUserVendorPermissions = async (body: IPostUserVendorPermissionsBodyDto) => {
  return plainToInstance(
    IUserVendorPermissionResponse,
    await instance(`userVendorPermissions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postUserInsurancePermissions = async (body: IPostUserInsurancePermissionsBodyDto) => {
  return plainToInstance(
    IUserInsurancePermissionResponse,
    await instance(`userInsurencePermissions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postUserOperationPermissions = async (body: IPostUserOperationPermissionsBodyDto) => {
  return plainToInstance(
    IUserOperationsPermissionResponse,
    await instance(`userOperationPermissions`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postRoute = async (body: IPostRouteBodyDto) => {
  return plainToInstance(
    IRouteResponse,
    await instance(`route`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postVehicleCategory = async (body: IPostVehicleCategoryBodyDto) => {
  return plainToInstance(
    IVehicleCategoryResponse,
    await instance(`vehicles/categories`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postVehiclePlan = async (body: IPostVehiclePlanBodyDto) => {
  return plainToInstance(
    IVehiclePlanResponse,
    await instance(`vehiclePlan`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postBulkChange = async (body: IPostBulkChangeBodyDto) => {
  return plainToInstance(
    IBulkChangeResponse,
    await instance(`bulkChange`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postVehicleRequest = async (body: IPostVehicleRequestBodyDto) => {
  return plainToInstance(
    IVehicleRequestResponse,
    await instance(`requestVehicle`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

// ── Paket Alım Talebi ──
export const postPackageRequest = async (body: IPostPackageRequestBodyDto) => {
  return plainToInstance(
    IPackageRequestResponse,
    await instance(`package-request`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const processPackageRequest = async (id: string) => {
  return plainToInstance(IPackageRequestResponse, await instance(`package-request/${id}/process`, { method: 'POST' }));
};

export const completePackageRequest = async (id: string) => {
  return plainToInstance(IPackageRequestResponse, await instance(`package-request/${id}/complete`, { method: 'POST' }));
};

export const cancelPackageRequest = async (id: string) => {
  return plainToInstance(IPackageRequestResponse, await instance(`package-request/${id}/cancel`, { method: 'POST' }));
};

export const recordPackageRequestBarcodes = async (
  id: string,
  entries: { packageId: number; shippingType: number; deliveryLocationId: string }[]
) => {
  return plainToInstance(
    IPackageRequestResponse,
    await instance(`package-request/${id}/barcodes`, {
      method: 'POST',
      body: JSON.stringify({ entries })
    })
  );
};

export const redirectPackageRequestVehicle = async (id: string, vehiclePlanId: string) => {
  return plainToInstance(
    IPackageRequestResponse,
    await instance(`package-request/${id}/redirect-vehicle`, {
      method: 'POST',
      body: JSON.stringify({ vehiclePlanId })
    })
  );
};

export const postSupplier = async (body: IPostSupplierBodyDto) => {
  return plainToInstance(
    ISupplierResponse,
    await instance(`supplier`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postContainers = async (body: IContainersPostDto) => {
  return await plainToInstance(
    IContainersResponse,
    instance(`containers`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postVehiclePlanVehicleStatus = async (id: string | undefined, body: IVehiclePlanVehicleStatusPostBodyDto) => {
  return await instance(`vehiclePlan/${id}/status`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postMission = async (body: IMissionPostDto) => {
  return await instance(`mission`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postDoor = async (body: IDoorPostDto) => {
  return await instance(`gate`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPersonel = async (body: IPersonelPostDto) => {
  return await instance(`personel`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPersonelLog = async (body: IPersonelLogPostDto) => {
  return await instance(`personelLog`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPersonelLogEntry = async (body: IPersonelLogEntryPostDto) => {
  return await instance(`personelLog/entry`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPersonelLogExit = async (body: IPersonelLogExitPostDto) => {
  return await instance(`personelLog/exit`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postLostPackageDescription = async (id: string | undefined, body: ILostPackageDescriptionPostBodyDto) => {
  return await instance(`lostPackages/${id}/description`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAcceptLossCase = async (id: string, body: IAcceptLossCaseBodyDto) => {
  return await instance(`lostPackages/${id}/accept`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postTransitionLossCase = async (id: string, body: ITransitionLossCaseBodyDto) => {
  return await instance(`lostPackages/${id}/transition`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCancelLossCase = async (id: string, body: ICancelLossCaseBodyDto) => {
  return await instance(`lostPackages/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

/** Hesapsız belge yükleme: presigned URL al (token'lı) */
export const postPublicLossDocumentUpload = async (
  token: string,
  body: ILossPublicUploadRequestBodyDto
): Promise<ILossPublicUploadStarted> => {
  return await instance(`lostPackages/public/document-requests/${encodeURIComponent(token)}/uploads`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCreateFromShipment = async (body: IFromShipmentBodyDto) => {
  return await instance(`lostPackages/from-shipment`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCreateDocumentRequest = async (id: string, body: ICreateDocumentRequestBodyDto) => {
  return await instance(`lostPackages/${id}/document-requests`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCreateLossBarcode = async (body: ICreateLossBarcodeBodyDto) => {
  return await instance(`lostPackages/loss-barcode`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postReopenLossCase = async (id: string, body: IReopenLossCaseBodyDto) => {
  return await instance(`lostPackages/${id}/reopen`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCompanyContacts = async (body: ICompanyContactsPostDto) => {
  return await instance(`companyContacts`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postApps = async (body: IAppsPostDto) => {
  return await instance(`apps`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postFordIntegrationFetch = async (body: IFordIntegrationFetchQueryDto) => {
  return plainToInstance(
    IFordIntegrationFetchListResponse,
    await instance(`entegrasyon/ford/fetch`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postFordIntegrationParse = async (body: IFordIntegrationFetchQueryDto): Promise<IFordIntegrationParseListResponse> => {
  return await instance(`entegrasyon/ford/parse`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAnnouncement = async (body: IAnnouncementPostDto): Promise<IAnnouncementResponse> => {
  return await instance(`announcement`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postDocument = async (body: IDocumentPostDto): Promise<IDocumentResponse> => {
  return await instance(`document`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postFileUploadDocument = async (id: number | string) => {
  return await instance(`document/${id}/upload`, {
    method: 'POST'
  });
};

export const postInviteAccept = async (id: string): Promise<IInviteResponse> => {
  return await instance(`userInvite/${id}/accept`, {
    method: 'POST'
  });
};

export const postInviteReject = async (id: string): Promise<IInviteResponse> => {
  return await instance(`userInvite/${id}/reject`, {
    method: 'POST'
  });
};

export const postNewUserAccept = async (id: string, body: INewUserAcceptPostDto, token: string): Promise<IInviteResponse> => {
  return await instance(`userInvite/${id}/newuseraccept?token=${token}`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAgreements = async (body: IAgreementsPostDto): Promise<IAgreementsResponse> => {
  return await instance(`agreements`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

/** Sözleşme onayını geri al (yalnız SUPERADMIN; BE guard) */
export const unapproveAgreement = async (id: string): Promise<IAgreementsResponse> => {
  return await instance(`agreements/${id}/unapprove`, { method: 'POST' });
};

export const postWebhooks = async (body: IPostWebhooksBodyDto): Promise<IWebhooksResponse> => {
  return await instance(`webhooks`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postRegionDetail = async (body: IRegionDetailPostDto): Promise<IRegionDetailResponse> => {
  return await instance(`regionDetail`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postRegionContactDetail = async (body: IRegionContactDetailPostDto): Promise<IRegionContactDetailResponse> => {
  return await instance(`regionContactDetail`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthVerifySmsStart = async (body: IPostAuthSmsStart): Promise<IAuthSmsStartResponse> => {
  return await instance(`auth/verify/sms/start`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthVerifySmsVerify = async (body: IPostAuthSmsVerify): Promise<IAuthSmsVerifyResponse> => {
  return await instance(`auth/verify/sms/verify`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthVerifyEmailStart = async (body: IPostAuthEmailStart): Promise<IAuthEmailStartResponse> => {
  return await instance(`auth/verify/email/start`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthVerifyEmailVerify = async (body: IPostAuthEmailVerify): Promise<IAuthEmailVerifyResponse> => {
  return await instance(`auth/verify/email/verify`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthForgot = async (body: IPostAuthForgot): Promise<void> => {
  return await instance(`auth/forgot`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postAuthForgotEnd = async (body: IPostAuthForgotEnd): Promise<void> => {
  return await instance(`auth/forgotEnd`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postCallForwarding = async (body: IPostCallForwardingBodyDto): Promise<void> => {
  return await instance(`call-forwarding`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postPackageTanzim = async (body: IPackageTanzimPostDto): Promise<void> => {
  return await instance(`packageTanzim`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postRegionTransferTypes = async (body: RegionTransferTypesCreate): Promise<void> => {
  return await instance(`region-transfer-types`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postTransferVehicleEntry = async (body: TransferVehicleEntryCreate): Promise<TransferVehicleEntryResponse> => {
  return await instance(`transfer-vehicle-entry`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postChatMessagesList = async (body: CreateChatMessageRequest): Promise<ChatMessagesListResponse> => {
  return await instance(`chat-messages-list`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postChatBotConversation = async (body: ChatBotCreateConversationRequest): Promise<ChatBotConversationResponse> => {
  return await instance(`ai-chatbot/conversations`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postChatBotMessage = async (body: ChatBotMessageRequest): Promise<ChatBotMessageResponse> => {
  return await instance(`ai-chatbot/messages`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postMobilizVehicleSync = async (): Promise<void> => {
  return await instance(`mobiliz/vehicles/sync`, {
    method: 'POST'
  });
};

export const postMobilizFleetSync = async (): Promise<void> => {
  return await instance(`mobiliz/fleets/sync`, {
    method: 'POST'
  });
};

export const postPackageFiles = async (data: PackageFilesPostDto): Promise<PackageFilesPostResponseDto> => {
  return await instance(`package-files`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const postDistrict = async (body: CreateDistrictDto): Promise<DistrictResponseDto> => {
  return plainToInstance(
    DistrictResponseDto,
    await instance(`districts`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postMultiSessionUser = async (body: CreateMultiSessionUserDTO): Promise<MultiSessionUserResponse> => {
  return plainToInstance(
    MultiSessionUserResponse,
    await instance(`multi-session-users`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );
};

export const postConvertDeliveredToReturn = async (
  packageId: number,
  body: IConvertDeliveredToReturnBodyDto
): Promise<IConvertDeliveredToReturnResponse> => {
  return await instance(`package/${packageId}/convert-to-return`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postBulkEmail = async (body: IBulkEmailPostDto): Promise<IBulkEmailResponse> => {
  return await instance(`bulk-email`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const postBulkEmailSuggest = async (body: IBulkEmailSuggestRequestDto): Promise<IBulkEmailSuggestResponse> => {
  return await instance(`bulk-email/suggest`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};
